import React from 'react';
import { ContentSection } from '../components/content-section';
import { Layout } from '../components/page-layout';
import styled, { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  body {
    color: #222;
    background: #fff;
    font-size: 16px;
    line-height: 1.7;
    letter-spacing: 0;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 500;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  letter-spacing: -0.01em;
  color: #18181b;
`;

const SubText = styled.div`
  font-size: 1rem;
  color: #6b7280;
  margin-top: 0.5rem;
  margin-bottom: 2.5rem;
`;

const HighlightSection = styled.div`
  padding: 1.5rem;
  margin-top: 3rem;
  background: #F87B15;
  color: #fff;
  border-radius: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HighlightText = styled.p`
  text-align: center;
  color: #fff;
  line-height: 1.5;
`;


const HighlightButton = styled.a`
  background: #fff;
  color: #F87B15;
  border-radius: 9999px;
  padding: 0.5rem 2rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  font-size: 1rem;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #ffe3d0;
    color: #d65c00;
  }
`;

const StyledUl = styled.ul`
  margin-left: 1.5rem;
  margin-bottom: 1rem;
  list-style: disc inside;
`;

const StyledLi = styled.li`
  margin-bottom: 0.25rem;
  font-weight: 300;
`;

const StyledP = styled.p`
  margin-bottom: 0.5rem;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: #737373;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;



export default function TermsOfUse() {
  return (
    <>
      <GlobalStyle />
      <Layout showCommonFooter={true}>
        <Container>
          <Title>Sensforge Terms of Use</Title>
          <SubText>Last update: Jan 1st 2025</SubText>

          <ContentSection title="1. Introduction">
            <StyledP>
              This Terms of Use (hereinafter the "Agreement") is a binding agreement between you (hereinafter referred to as "you" or "user") and Sensforge Inc., along with its affiliates (collectively, "Sensforge," "we," "us," or "our"). This Agreement governs your access to and use of Sensforge's websites, applications, and services that reference or link to this Agreement (collectively, the "Services"). Before creating an account or using Sensforge's Services, please carefully read and fully understand the terms and conditions outlined in this Agreement, particularly provisions regarding service fees, applicable laws, dispute resolution, liability limitations, and your rights and obligations. These important provisions are highlighted in bold for your convenience.
            </StyledP>
            <StyledP>
              By clicking "Agree," creating an account, or using any Sensforge Services, you confirm that you have read, understood, and agreed to this Agreement. If you do not agree to the terms of this Agreement, you must immediately cease using Sensforge's Services.
            </StyledP>
          </ContentSection>

          <ContentSection title="2. User Account: Accuracy and Security">
            <StyledP />
          </ContentSection>

          <ContentSection title="2.1 Creating an Account">
            <StyledP>
              To access certain features of Sensforge's Services, such as those on sensforge.com, the Sensforge App, or associated devices, you may be required to create an account ("Account") and provide personally identifiable information.
            </StyledP>
            <StyledP>
              Once created, your Account can be used to log into multiple Sensforge platforms without requiring additional registration.
            </StyledP>
          </ContentSection>

          <ContentSection title="2.2 Information Accuracy">
            <StyledP>
              You represent and warrant that the information you provide when creating or maintaining your Account is current, complete, and accurate. Sensforge may use this information solely for identity verification purposes as outlined in our Privacy Policy.
            </StyledP>
            <StyledP>
              You agree to update your information promptly to ensure its completeness and accuracy. Submitting false or misleading information (e.g., impersonating another person or creating fictitious profiles) is prohibited. If we determine, in our sole discretion, that the information you provided is inaccurate, incomplete, or misleading, we reserve the right to suspend or terminate your Account and deny you access to our Services. For more details on how your data is handled, please refer to our Privacy Policy.
            </StyledP>
          </ContentSection>

          <ContentSection title="2.3 Account Security">
            <StyledP>
              You are responsible for maintaining the confidentiality of your Account credentials, including your username and password. You agree not to:
            </StyledP>
            <StyledUl>
              <StyledLi>Use the credentials of another person or share your username and password.</StyledLi>
              <StyledLi>Circumvent any authentication systems or security mechanisms designed to restrict access to Sensforge's Services.</StyledLi>
            </StyledUl>
            <StyledP>
              You must notify Sensforge immediately of any unauthorized access to your Account or breach of security. While we take steps to protect your Account, we are not liable for any losses you incur due to unauthorized use. However, you may be held accountable for losses suffered by Sensforge or others resulting from unauthorized use of your Account.
            </StyledP>
          </ContentSection>

          <ContentSection title="3. Privacy Policy & Cookies">
            <StyledP>
              By using Sensforge's Services, you acknowledge that you have read, understood, and agree to be bound by our Privacy Policy (the "Privacy Policy"). As outlined in the Privacy Policy, Sensforge's websites use cookies to collect certain information from you. Before using Sensforge's websites, please review our Cookie Notice carefully to understand how cookies are used and managed.
            </StyledP>
          </ContentSection>

          <ContentSection title="4. Eligibility">
            <StyledP>
              By accessing or using Sensforge's Services, you represent and warrant that:
            </StyledP>
            <StyledUl>
              <StyledLi>You are at least eighteen (18) years of age or equivalent minimum age in the relevant jurisdiction.</StyledLi>
              <StyledLi>You are legally qualified to enter and form binding contracts under applicable law.</StyledLi>
            </StyledUl>
            <StyledP>
              If you are using Sensforge's Services on behalf of a business entity, you further represent and warrant that you are authorized to act on behalf of and enter contracts for that entity.
            </StyledP>
          </ContentSection>

          <ContentSection title="5. Purchases & Payments">
            <StyledP />
          </ContentSection>
          <ContentSection title="5.1 Payment Information">
            <StyledP>
              When purchasing products through Sensforge's websites or applications, you may be required to provide billing and shipping information, as well as details of your credit or debit card (each, a "Payment Card"). By submitting Payment Card information, you represent and warrant that:
            </StyledP>
            <StyledUl>
              <StyledLi>You are the authorized account holder for the Payment Card.</StyledLi>
              <StyledLi>You authorize us to charge your Payment Card for the cost of the products, including applicable taxes, shipping, and handling fees as communicated at the time of purchase.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="5.2 Order Process and Acceptance">
            <StyledP>
              Your order constitutes an offer to purchase the product(s). Upon placing an order, you will receive an Order Confirmation Email, which acknowledges that we have received your order but does not confirm acceptance of your offer.
            </StyledP>
            <StyledP>
              The contract of sale is only finalized when we dispatch the product(s) to you and send you a confirmation email indicating that your product(s) have been shipped.
            </StyledP>
          </ContentSection>

          <ContentSection title="5.3 Returns and Refunds">
            <StyledP>
              If you are not satisfied with a product purchased through Sensforge's websites or applications, you may return it within thirty (30) days of the purchase date for a refund, provided it is in its original packaging. To initiate a return or refund, please <a href="mailto:legal@sensforge.com">contact us</a> through Sensforge's websites or applications. After the 30-day refund window, your sole recourse will be through our warranty policy, subject to its terms and conditions.
            </StyledP>
          </ContentSection>

          <ContentSection title="5.4 Third-Party Payment Platforms">
            <StyledP>
              At our sole discretion, you may complete purchases through third-party platforms such as Amazon.com ("Amazon") or PayPal.com("PayPal"). If you choose to use these platforms, you understand and agree that:
            </StyledP>
            <StyledUl>
              <StyledLi>Amazon and PayPal are third parties ("Third Parties") as defined in this Agreement.</StyledLi>
              <StyledLi>Your purchase may be subject to the terms of service, privacy policies, refund policies, and other agreements of these Third Parties.</StyledLi>
              <StyledLi>It is your responsibility to review and understand the applicable policies of these Third Parties before completing your purchase.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="6. License to Use Service">
            <StyledP>
              Subject to your compliance with this Agreement, we grant you a limited, non-exclusive, non-sublicensable, revocable, and non-transferable license to access and use Sensforge's Services for your personal use. This license does not permit you to:
            </StyledP>
            <StyledUl>
              <StyledLi>Republish material from Sensforge's Services, including republication on another platform, or sell, rent, or sublicense material from Sensforge's Services.</StyledLi>
              <StyledLi>Display any material from Sensforge's Services in public.</StyledLi>
              <StyledLi>Reproduce, duplicate, copy, or otherwise exploit material from Sensforge's Services for commercial purposes.</StyledLi>
              <StyledLi>Edit, modify, or create derivative works of any material on Sensforge's Services.</StyledLi>
              <StyledLi>Redistribute material from Sensforge's Services unless such material is specifically and expressly designated as available for redistribution.</StyledLi>
            </StyledUl>
            <StyledP>
              Unless explicitly stated otherwise, we and/or our licensors own all intellectual property rights in Sensforge's Services and their content. All rights not expressly granted under this Agreement are reserved by us.
            </StyledP>
          </ContentSection>

          <ContentSection title="7. Assumption of Risk">
            <StyledP>
              By using Sensforge's Services, you knowingly and voluntarily assume all risks associated with such use. You agree, on behalf of yourself, your heirs, personal representatives, and assigns, to release, waive, discharge, hold harmless, defend, and indemnify Sensforge, including its owners, officers, directors, employees, agents, affiliates, consultants, sublicensees, successors, assigns, parents, subsidiaries, and related entities (collectively, the "Sensforge Company"), from any and all claims, actions, losses, or damages. This includes, but is not limited to:
            </StyledP>
            <StyledUl>
              <StyledLi>Bodily injury</StyledLi>
              <StyledLi>Property damage</StyledLi>
              <StyledLi>Wrongful death</StyledLi>
              <StyledLi>Emotional distress</StyledLi>
              <StyledLi>Loss of privacy</StyledLi>
              <StyledLi>Any other harm, whether to you or third parties, arising out of or in connection with your use of Sensforge's Services.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="8. Prohibited Conduct">
            <StyledP>
              To maintain the integrity and security of Sensforge's Services, we strictly prohibit the following activities. Violation of these restrictions may result in civil and/or criminal liability.
            </StyledP>
          </ContentSection>

          <ContentSection title="8.1 Prohibited Uses">
            <StyledP>You must not:</StyledP>
            <StyledUl>
              <StyledLi>Use Sensforge's Services in a manner that damages, disables, overburdens, or impairs its functionality or accessibility.</StyledLi>
              <StyledLi>Engage in any activity that is unlawful, illegal, fraudulent, harmful, or related to any such purposes.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="8.2 Malicious Software and Data Collection">
            <StyledP>You must not:</StyledP>
            <StyledUl>
              <StyledLi>Upload, distribute, or link to spyware, viruses, worms, keystroke loggers, rootkits, or other malicious software on Sensforge's Services.</StyledLi>
              <StyledLi>Conduct systematic or automated data collection activities (e.g., scraping, data mining, data extraction) on or related to Sensforge's Services without our prior written consent.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="8.3 Unauthorized Access and Misuse">
            <StyledP>You must not:</StyledP>
            <StyledUl>
              <StyledLi>Provide false, misleading, or inaccurate information to Sensforge or other users.</StyledLi>
              <StyledLi>Impersonate or misrepresent your affiliation with any person or entity.</StyledLi>
              <StyledLi>Access or attempt to access accounts, content, or data you are not authorized to access.</StyledLi>
              <StyledLi>Probe, scan, or test the vulnerability of Sensforge's Services or associated networks, or breach security/authentication measures.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="8.4 Unauthorized Access and Misuse">
            <StyledP>You must not:</StyledP>
            <StyledUl>
              <StyledLi>Interfere with or disrupt the experience of other users, hosts, or networks using Sensforge's Services. This includes submitting malware, exploiting software vulnerabilities, or engaging in denial-of-service attacks.</StyledLi>
              <StyledLi>Forge, modify, or falsify network headers, packets, or metadata in transmissions involving Sensforge's Services.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="8.5 Unethical Practices">
            <StyledP>You must not:</StyledP>
            <StyledUl>
              <StyledLi>Create additional accounts to manipulate or promote your (or another's) business or cause others to do so.</StyledLi>
              <StyledLi>Pay or incentivize others for interactions, engagements, or activities on Sensforge's Services.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="8.6 Unsolicited Communications and Marketing">
            <StyledP>You must not:</StyledP>
            <StyledUl>
              <StyledLi>Transmit or send unsolicited commercial communications (e.g., spam).</StyledLi>
              <StyledLi>Use Sensforge's Services for marketing purposes without our express written consent.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="9. User Content">
            <StyledP />
          </ContentSection>

          <ContentSection title="9.1 Definition of User Content">
            <StyledP>
              In this Agreement, "your user content" refers to any material you submit to Sensforge's Services for any purpose, including but not limited to text, images, audio, video, or audio-visual materials.
            </StyledP>
          </ContentSection>

          <ContentSection title="9.2 License to Sensforge">
            <StyledP>
              By submitting user content, you grant Sensforge a worldwide, irrevocable, non-exclusive, royalty-free license to use, reproduce, adapt, publish, translate, and distribute your user content in any existing or future media. You also grant us the right to sub-license these rights and the right to take action against infringement of these rights. However, we will not take legal action regarding the infringement of product reviews you post without your explicit permission.
            </StyledP>
          </ContentSection>

          <ContentSection title="9.3 Restrictions on User Content">
            <StyledP>Your user content must not:</StyledP>
            <StyledUl>
              <StyledLi>Be illegal, unlawful, or infringe on any third party's rights.</StyledLi>
              <StyledLi>Be capable of giving rise to legal action against you, Sensforge, or any third party under applicable laws.</StyledLi>
            </StyledUl>
            <StyledP>
              You may not submit user content that has been the subject of threatened or actual legal proceedings or complaints.
            </StyledP>
          </ContentSection>

          <ContentSection title="9.4 Rights Reserved by Sensforge">
            <StyledP>
              Sensforge reserves the right to edit or remove any material submitted to our Services, stored on our servers, or hosted or published through our platforms. However, Sensforge does not undertake to monitor all user content submissions or publications actively.
            </StyledP>
          </ContentSection>

          <ContentSection title="10. No Warranties; Limitation of Liability">
            <StyledP />
          </ContentSection>

          <ContentSection title="10.1 No Warranties">
            <StyledP>
              Sensforge, including our licensors and suppliers, disclaims any and all warranties, express or implied, regarding Sensforge's Services. This includes, but is not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement, or title.
            </StyledP>
            <StyledP>We do not guarantee that Sensforge's Services will:</StyledP>
            <StyledUl>
              <StyledLi>Meet your requirements.</StyledLi>
              <StyledLi>Operate uninterrupted or error-free.</StyledLi>
            </StyledUl>
            <StyledP>
              We disclaim all implied liability for damages arising from Sensforge's Services, including but not limited to mistakes, omissions, interruptions, delays, or tortious conduct. We shall not be liable for indirect, incidental, special, consequential, or punitive damages, including lost profits or revenues.
            </StyledP>
          </ContentSection>

          <ContentSection title="10.2 Your Responsibility for Loss or Damage">
            <StyledP>
              You use Sensforge's Services at your own risk. Sensforge is not responsible for any loss or damage to your devices, data, or information caused by your access to or use of our Services. Sensforge's Services may contain bugs, errors, or other limitations, and you agree to assume all associated risks.
            </StyledP>
          </ContentSection>

          <ContentSection title="10.3 Limitation of Liability">
            <StyledP>
              In no event shall Sensforge, its affiliates, licensors, or suppliers be liable for:
            </StyledP>
            <StyledUl>
              <StyledLi>Special, incidental, or consequential damages.</StyledLi>
              <StyledLi>Loss of profits, data, confidential or other information, privacy, or substitute goods or services.</StyledLi>
              <StyledLi>Breaches of duty, negligence, or failure to meet reasonable care standards.</StyledLi>
            </StyledUl>
            <StyledP>
              This limitation applies regardless of the foreseeability of damages and forms a fundamental basis of the agreement between you and Sensforge.
            </StyledP>
          </ContentSection>

          <ContentSection title="10.4 Application of Disclaimers">
            <StyledP>
              Some jurisdictions may not allow the exclusion of certain warranties or limitation of damages. In such cases, certain disclaimers and limitations may not apply to you. Our affiliates, licensors and suppliers are intended third-party beneficiaries of these disclaimers and limitations.
            </StyledP>
            <StyledP>
              No oral or written advice obtained by you from Sensforge or through our Services alters the disclaimers or limitations set forth herein.
            </StyledP>
          </ContentSection>

          <ContentSection title="10.5 No Advice Provided">
            <StyledP>
              Sensforge's Services do not constitute legal, financial, medical, or other professional advice. If you require such advice, you should consult an appropriate professional.
            </StyledP>
          </ContentSection>

          <ContentSection title="11. Consent to Receive Electronic Communications from Us">
            <StyledP>
              By creating an account and providing your email address, you expressly consent to receive electronic communications from Sensforge. These communications may include:
            </StyledP>
            <StyledUl>
              <StyledLi>Information related to your purchases or customer service inquiries.</StyledLi>
              <StyledLi>Notifications about Sensforge's Services, promotions, and other content that may interest you, subject to your prior opt-in consent where required by applicable law.</StyledLi>
            </StyledUl>
            <StyledP>
              You may unsubscribe from promotional emails at any time by clicking the unsubscribe link in any promotional email or visiting https://www.sensforge.com/contact-us. After unsubscribing, you may still receive necessary communications related to your account, purchases, or services you have requested. To unsubscribe from promotional text messages, reply "T" via text. After unsubscribing, necessary service-related messages may still be sent when required.
            </StyledP>
          </ContentSection>

          <ContentSection title="12. Intellectual Property">
            <StyledP />
          </ContentSection>

          <ContentSection title="12.1 Compliance with Intellectual Property Laws">
            <StyledP>
              You agree to comply with all applicable intellectual property laws while using Sensforge's Services. You must not upload, post, transmit, or distribute any content that infringes on the intellectual property rights of others, including copyrights, trademarks, or proprietary rights.
            </StyledP>
          </ContentSection>

          <ContentSection title="12.2 Trademarks">
            <StyledP>
              The name "Sensforge" and associated logos (the "Marks") are trademarks or registered trademarks of Sensforge Companies and may not be used without our permission. Third-party trademarks, logos, or domain names visible on Sensforge's Services are the property of their respective owners.
            </StyledP>
            <StyledP>
              You acquire no rights, title, or interest in the Marks or any third-party trademarks through your use of Sensforge's Services. Any goodwill arising from your use of the Marks inures to the benefit of Sensforge Companies. You agree not to challenge the ownership, validity, or enforceability of the Marks.
            </StyledP>
          </ContentSection>

          <ContentSection title="12.3 Copyrights">
            <StyledP />
          </ContentSection>

          <ContentSection title="12.3.1 Ownership">
            <StyledP>
              All content available on Sensforge's Services—including logos, text, graphics, and their arrangement—is owned by Sensforge Companies or its licensors. Except as explicitly permitted, your use of Sensforge's Services does not grant you any rights to such materials.
            </StyledP>
          </ContentSection>

          <ContentSection title="12.3.2 Reporting Copyright Infringement">
            <StyledP>
              If you believe your copyrighted material is being unlawfully used on Sensforge's Services, you may submit a DMCA Notice to our designated copyright agent. The written notice must include:
            </StyledP>
            <StyledUl>
              <StyledLi>Your physical or electronic signature.</StyledLi>
              <StyledLi>Identification of the copyrighted work(s) allegedly infringed.</StyledLi>
              <StyledLi>Identification of the infringing material and its location on Sensforge's Services (e.g., URL or Thing ID).</StyledLi>
              <StyledLi>Your contact information (name, address, phone number, and email).</StyledLi>
              <StyledLi>A statement affirming your good-faith belief that the use of the material is unauthorized.</StyledLi>
              <StyledLi>A statement under penalty of perjury that the information provided is accurate and that you are authorized to act on behalf of the copyright owner.</StyledLi>
            </StyledUl>
            <StyledP>Designated Agent:</StyledP>
            <StyledUl>
              <StyledLi>Email: copyright@sensforge.com</StyledLi>
            </StyledUl>
            <StyledP>
              If the original DMCA Notice filer does not initiate legal action within ten (10) business days of your Counter Notice, the removed content may be restored. Misrepresentation of claims may result in liability under Section 512(f) of the DMCA.
            </StyledP>
          </ContentSection>

          <ContentSection title="12.3.3 Uploading Content">
            <StyledP>
              When uploading content (e.g., files, links, or comments) to Sensforge's Services, you represent and warrant that:
            </StyledP>
            <StyledUl>
              <StyledLi>The content is your original creation or you hold all necessary rights to upload it.</StyledLi>
              <StyledLi>The content does not infringe on third-party rights or violate local laws.</StyledLi>
            </StyledUl>
            <StyledP>
              Sensforge reserves the right to reject, remove, or limit access to uploaded content at its discretion.
            </StyledP>
          </ContentSection>

          <ContentSection title="12.4 Links to External Sites">
            <StyledP>
              Users may post links on Sensforge's Services. Sensforge is not responsible for the content or reliability of linked sites and does not endorse their content. Use external links at your own discretion.
            </StyledP>
          </ContentSection>

          <ContentSection title="12.5 Downloading Files">
            <StyledP>
              Sensforge cannot guarantee that files available for download are free of viruses, worms, or other harmful code. You are responsible for implementing safeguards, including antivirus software, to protect your systems and data.
            </StyledP>
          </ContentSection>

          <ContentSection title="13. Reasonableness">
            <StyledP>
              By using Sensforge's Services, you acknowledge and agree that the exclusions and limitations of liability set forth in this Agreement are reasonable.
            </StyledP>
            <StyledP>
              If you do not consider these terms reasonable, you must not use Sensforge's Services.
            </StyledP>
          </ContentSection>

          <ContentSection title="14. Other Parties">
            <StyledP />
          </ContentSection>

          <ContentSection title="14.1 Third-Party Services">
            <StyledP>
              Sensforge's Services may link to or integrate with third-party services ("Third Party Services"), some of which may have established relationships with us while others may not. We do not control, review, or endorse the content, performance, or offerings of Third Party Services ("Third Party Services"), including their software, goods, or services.
            </StyledP>
            <StyledP>Accordingly, we make no representations or warranties about:</StyledP>
            <StyledUl>
              <StyledLi>The accuracy, currency, content, legality, quality, or fitness of any information, materials, goods, or services provided through Third Party Services.</StyledLi>
              <StyledLi>The performance or availability of Third Party Services.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="14.2 Limited Liability for Sensforge and Affiliates">
            <StyledP>
              As a limited liability entity, we have a legitimate interest in limiting the personal liability of our officers, employees, and affiliates. By using Sensforge's Services, you agree not to bring any claim personally against:
            </StyledP>
            <StyledUl>
              <StyledLi>Sensforge officers, employees, or agents.</StyledLi>
              <StyledLi>Sensforge affiliates, subsidiaries, successors, assigns, or subcontractors.</StyledLi>
            </StyledUl>
            <StyledP>
              You further agree that the limitations of warranties and liability set out in this Agreement apply to and protect these parties to the fullest extent permitted by law.
            </StyledP>
          </ContentSection>

          <ContentSection title="15. Indemnity">
            <StyledP>
              Without limiting any other indemnification provision of this Agreement, you ("Indemnitor") agree to defend, indemnify, and hold harmless Sensforge and its affiliates (collectively, "Indemnitees") from and against any and all claims, actions, demands, and other proceedings ("Claims"), including but not limited to reasonable legal fees and costs, arising out of or related to:
            </StyledP>
            <StyledUl>
              <StyledLi>Your relationship with Sensforge, whether based on contract, tort, statute, fraud, misrepresentation, or any other legal theory.</StyledLi>
              <StyledLi>Your breach of this Agreement, including any representations or warranties contained herein.</StyledLi>
              <StyledLi>Your use of Sensforge's Services or Products.</StyledLi>
              <StyledLi>Your provision of information or data to Sensforge or any Indemnitee.</StyledLi>
              <StyledLi>Your violation or alleged violation of any applicable law or regulation, whether domestic, international, federal, state, or local.</StyledLi>
              <StyledLi>Your violations of Section 8 (Prohibited Conduct).</StyledLi>
              <StyledLi>Your infringement or alleged infringement of any third party's copyrights, trademarks, or other intellectual property rights.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="15.1 Control of Defense">
            <StyledP>
              You agree that Sensforge has the sole and exclusive right to control the defense of any Claim, including the selection of legal counsel and all related settlement negotiations.
            </StyledP>
          </ContentSection>

          <ContentSection title="15.2 Participation Rights">
            <StyledP>
              Each Indemnitee reserves the right, at its own expense, to participate in the defense of any Claim for which you are required to provide indemnification. However, such participation does not relieve you of your obligations under this section.
            </StyledP>
          </ContentSection>

          <ContentSection title="15.3 Settlement Restrictions">
            <StyledP>
              You may not settle any Claim without prior written consent from the relevant Sensforge Companies.
            </StyledP>
          </ContentSection>

          <ContentSection title="16. Termination">
            <StyledP />
          </ContentSection>

          <ContentSection title="16.1 Termination Rights">
            <StyledP>
              We reserve the right, at our sole discretion and without prior notice or liability, to deny access to or terminate your use of Sensforge's Services for any reason or no reason, including but not limited to:
            </StyledP>
            <StyledUl>
              <StyledLi>Breach or suspected breach of any representation, warranty, or covenant in this Agreement.</StyledLi>
              <StyledLi>Violation of any applicable law or regulation.</StyledLi>
            </StyledUl>
            <StyledP>
              This Agreement shall automatically terminate if you breach any of its terms, including representations, warranties, or covenants. Termination shall be automatic and will not require any action on our part.
            </StyledP>
          </ContentSection>

          <ContentSection title="16.2 Effect of Termination">
            <StyledP>Upon termination:</StyledP>
            <StyledUl>
              <StyledLi>All rights and licenses granted under this Agreement, including your right to use Sensforge's Services, shall immediately cease.</StyledLi>
              <StyledLi>We may, but are not obligated to, rescind any Services provided to you and delete any of your Personal Information or related files from our systems.</StyledLi>
              <StyledLi>You must discontinue all use of Sensforge's Services.</StyledLi>
              <StyledLi>We reserve the right to take any measures necessary to prevent unauthorized use of Sensforge's Services following termination, including but not limited to:</StyledLi>
              <StyledLi>Technological barriers such as IP blocking.</StyledLi>
              <StyledLi>Direct contact with your Internet Service Provider.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="16.3 Survival">
            <StyledP>
              Certain provisions of this Agreement will survive its termination, including but not limited to: Sections 1-4 and 7-19.
            </StyledP>
          </ContentSection>

          <ContentSection title="17. Dispute Resolution">
            <StyledP />
          </ContentSection>

          <ContentSection title="17.1 Governing Law">
            <StyledP>
              This Agreement shall be governed by and construed in accordance with the laws of the State of Pennsylvania without regard to its conflict of law principles.
            </StyledP>
          </ContentSection>

          <ContentSection title="17.2 Arbitration">
            <StyledP>
              Any dispute, controversy, or claim arising out of or relating to this Agreement, its breach, termination, or validity, shall be resolved through binding arbitration administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules.
            </StyledP>
            <StyledUl>
              <StyledLi>Arbitration Location: The arbitration shall take place in Pennsylvania, USA, unless both parties agree to another location.</StyledLi>
              <StyledLi>Arbitrators: The arbitration shall be conducted by a panel of three arbitrators appointed in accordance with the AAA rules.</StyledLi>
              <StyledLi>Language: The arbitration shall be conducted in English.</StyledLi>
              <StyledLi>Costs and Fees: Each party shall bear its own arbitration costs and fees, except as otherwise required by applicable law or awarded by the arbitrators.</StyledLi>
            </StyledUl>
          </ContentSection>

          <ContentSection title="17.3 Jurisdiction">
            <StyledP>
              For any matters that are not subject to arbitration under this Agreement, you agree to submit to the exclusive jurisdiction of the state and federal courts located in California, USA, and waive any objections to venue or jurisdiction in such courts.
            </StyledP>
          </ContentSection>

          <ContentSection title="17.4 Waiver of Class Actions">
            <StyledP>
              You agree that any dispute resolution proceedings shall be conducted on an individual basis and not as part of a class, consolidated, or representative action.
            </StyledP>
          </ContentSection>

          <ContentSection title="17.5 Time Limitations">
            <StyledP>
              Any claim you bring arising out of or relating to this Agreement must be filed within one (1) year after such claim arose, or it shall be permanently barred, unless otherwise required by law.
            </StyledP>
          </ContentSection>

          <ContentSection title="18. Notices">
            <StyledP>
              All notices required or permitted under this Agreement must be in writing.
            </StyledP>
            <StyledUl>
              <StyledLi>Arbitration Location: The arbitration shall take place in Pennsylvania, USA, unless both parties agree to another location.</StyledLi>
              <StyledLi>Arbitrators: The arbitration shall be conducted by a panel of three arbitrators appointed in accordance with the AAA rules.</StyledLi>
              <StyledLi>Language: The arbitration shall be conducted in English.</StyledLi>
              <StyledLi>Costs and Fees: Each party shall bear its own arbitration costs and fees, except as otherwise required by applicable law or awarded by the arbitrators.</StyledLi>
            </StyledUl>
            <StyledP>
              You are responsible for ensuring your email address on file is accurate and current.
            </StyledP>
          </ContentSection>

          <ContentSection title="19. Miscellaneous">
            <StyledP />
          </ContentSection>

          <ContentSection title="19.1 Assignment">
            <StyledP>
              We may assign, transfer, or subcontract our rights and obligations under this Agreement without prior notice or your consent. You may not assign, transfer, or subcontract any of your rights or obligations under this Agreement without our prior written consent.
            </StyledP>
          </ContentSection>

          <ContentSection title="19.2 Severability">
            <StyledP>
              If any provision of this Agreement is deemed unlawful or unenforceable by a court or other authority, the remaining provisions will remain in effect. If a portion of an unlawful or unenforceable provision can be modified to make it lawful or enforceable, that portion will be deemed removed or adjusted accordingly.
            </StyledP>
          </ContentSection>

          <ContentSection title="19.3 No Waiver">
            <StyledP>
              Failure by either party to enforce any provision of this Agreement or address a breach shall not constitute a waiver of that provision or any subsequent breach.
            </StyledP>
          </ContentSection>

          <ContentSection title="19.4 Independent Contractors">
            <StyledP>
              The relationship between you and Sensforge is solely that of independent contractors. Nothing in this Agreement creates an agency, partnership, joint venture, or employer-employee relationship.
            </StyledP>
          </ContentSection>

          <ContentSection title="19.5 No Third-Party Beneficiaries">
            <StyledP>
              This Agreement does not confer any rights or benefits on third parties, except as expressly stated for Sensforge's affiliates, "Indemnitees", licensors, and suppliers.
            </StyledP>
          </ContentSection>

          <ContentSection title="19.6 Entire Agreement">
            <StyledP>
              This Agreement, together with our Privacy Policies, constitutes the entire agreement between you and Sensforge regarding your use of Sensforge's Services and supersedes any prior agreements.
            </StyledP>
          </ContentSection>

          <ContentSection title="19.7 Changes to the Agreement">
            <StyledP>
              We reserve the right to modify this Agreement at any time. Changes will be effective upon updating the "Last Modified" date. For material changes, we will notify you per Section 18 (Notices).
            </StyledP>
          </ContentSection>

          <ContentSection title="19.8 Contact Us">
            <StyledP>
              For questions regarding this Agreement,{' '}
              <a href="mailto:legal@sensforge.com">contact us</a> at
              <a href="mailto:legal@sensforge.com">legal@sensforge.com</a>.
            </StyledP>
          </ContentSection>
          <HighlightSection>
            <HighlightText>
              For questions or concerns about this Terms of Use, <br />
              please contact us at: Email: legal@sensforge.com<br />
              Sensforge Inc.<br />
              639 Alpha Drive, Pittsburgh, PA 15238<br />
            </HighlightText>
            <HighlightButton
              href="mailto:legal@sensforge.com"
              type="button"
            >
              Contact Us
            </HighlightButton>
          </HighlightSection>
        </Container>
      </Layout>
    </>
  );
}
